import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/Card';
import { cn } from '@/components/ui/cn';

export interface LoadingScreenProps {
  onDone: () => void;
  /** Total duration in ms before onDone fires. Default 3000. */
  durationMs?: number;
  className?: string;
}

const DEFAULT_DURATION_MS = 3000;
const TICK_MS = 30;

/**
 * Transient "Analyzing…" screen between the last quiz answer and the result page.
 * Animated terra dots + progress bar over `durationMs`, then `onDone` fires once.
 */
export function LoadingScreen({
  onDone,
  durationMs = DEFAULT_DURATION_MS,
  className,
}: LoadingScreenProps) {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      setProgress(Math.min(100, (elapsed / durationMs) * 100));
    }, TICK_MS);
    const done = setTimeout(() => {
      setProgress(100);
      onDone();
    }, durationMs);
    return () => {
      clearInterval(tick);
      clearTimeout(done);
    };
  }, [durationMs, onDone]);

  return (
    <Card
      className={cn('w-full bg-paper-2', className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <CardContent className="flex flex-col items-center gap-6 px-6 py-12 text-center">
        <div className="flex gap-1.5" aria-hidden="true">
          <span className="block h-2.5 w-2.5 animate-bounce rounded-full bg-terra-600 [animation-delay:-0.3s]" />
          <span className="block h-2.5 w-2.5 animate-bounce rounded-full bg-terra-600 [animation-delay:-0.15s]" />
          <span className="block h-2.5 w-2.5 animate-bounce rounded-full bg-terra-600" />
        </div>
        <div>
          <h2 className="font-display text-2xl font-medium text-ink-900">
            {t('quiz.loading.title')}
          </h2>
          <p className="mt-2 text-sm text-ink-500">{t('quiz.loading.subtitle')}</p>
        </div>
        <div
          className="h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-ink-100"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
          aria-label={t('quiz.loading.title')}
        >
          <div
            className="h-full bg-terra-600 transition-all duration-100"
            style={{ width: `${progress}%` }}
            data-testid="loadingscreen-bar"
          />
        </div>
      </CardContent>
    </Card>
  );
}
